import React, { useMemo } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon } from 'lucide-react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import IconArrowUp from '@/assets/icons/icon-arrow-up-01.svg?react';

import { cn } from '@/lib/utils';

// Track last trigger rect so Content can decide side from viewport space
type LPRect = { top: number; bottom: number } | null;
let __lpLastSelectTriggerRect: LPRect = null;
const rememberTriggerRect = (el: HTMLElement) => {
  try {
    const r = el.getBoundingClientRect();
    __lpLastSelectTriggerRect = { top: r.top, bottom: r.bottom };
  } catch {
    __lpLastSelectTriggerRect = null;
  }
};

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  className,
  truncate = true,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value> & { truncate?: boolean }) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn(truncate ? 'truncate' : 'overflow-visible whitespace-nowrap', className)}
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  hideIcon = false,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
  hideIcon?: boolean;
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        'group text-button-sm relative flex h-8 w-full cursor-pointer items-center rounded-lg bg-neutral-50 p-2 pr-8 !text-neutral-900 disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:outline data-[state=open]:outline-indigo-500',
        className,
      )}
      onPointerDown={(e) => {
        rememberTriggerRect(e.currentTarget as HTMLElement);
        props.onPointerDown?.(e);
      }}
      onMouseDown={(e) => {
        rememberTriggerRect(e.currentTarget as HTMLElement);
        props.onMouseDown?.(e);
      }}
      {...props}
    >
      {children}
      {!hideIcon && (
        <SelectPrimitive.Icon>
          <IconArrowDown className="absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 transition-all group-aria-expanded:rotate-180" />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-pointer items-center justify-center py-1', className)}
      {...props}
    >
      <IconArrowUp className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex items-center justify-center py-1', className)}
      {...props}
    />
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  style: styleProp,
  side: sideProp,
  avoidCollisions: avoidCollisionsProp,
  truncate = true,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & { truncate?: boolean }) {
  // Ensure a dedicated top-level portal container with a very high stacking order
  const portalContainer = useMemo(() => {
    if (typeof document === 'undefined') return undefined;
    const id = 'lp-portal-top';
    let el = document.getElementById(id) as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      el.style.position = 'relative';
      el.style.zIndex = '3000000';
      document.body.appendChild(el);
    }
    return el;
  }, []);

  const calculateSideAndHeight = React.useCallback(() => {
    const rect = __lpLastSelectTriggerRect;
    if (!rect || typeof window === 'undefined')
      return { side: 'bottom' as const, maxHeight: undefined };

    const margin = 8;
    const viewportH = window.innerHeight || 0;
    const bottomSpace = Math.max(0, viewportH - rect.bottom - margin);
    const topSpace = Math.max(0, rect.top - margin);

    // Auto-detect best side unless explicitly disabled
    const shouldAutoDetect = avoidCollisionsProp !== false || !sideProp;
    const autoSide: 'bottom' | 'top' =
      bottomSpace >= 80 || bottomSpace >= topSpace ? 'bottom' : 'top';
    const effectiveSide: 'bottom' | 'top' = shouldAutoDetect
      ? autoSide
      : (sideProp as 'bottom' | 'top');
    const available = effectiveSide === 'bottom' ? bottomSpace : topSpace;

    return {
      side: effectiveSide,
      maxHeight: Math.max(112, available - 4),
    };
  }, [sideProp, avoidCollisionsProp]);

  const { side: calculatedSide, maxHeight: calculatedMaxHeight } = calculateSideAndHeight();

  return (
    <SelectPrimitive.Portal container={portalContainer}>
      <SelectPrimitive.Content
        data-slot="select-content"
        data-colorpicker-portal
        className={cn(
          'text-button-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-neutral-0 pointer-events-auto relative z-[3000001] min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border text-neutral-900 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.15)] outline outline-offset-[-1px] outline-neutral-100',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        style={{
          ...styleProp,
          zIndex: 4000000,
          maxWidth: truncate ? 'var(--radix-select-trigger-width)' : undefined,
          maxHeight: calculatedMaxHeight !== undefined ? `${calculatedMaxHeight}px` : undefined,
        }}
        side={sideProp ?? calculatedSide}
        sideOffset={4}
        avoidCollisions={typeof avoidCollisionsProp === 'boolean' ? avoidCollisionsProp : true}
        collisionPadding={8}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            truncate && 'max-w-[240px]',
            position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1',
            position === 'popper' && truncate && 'h-[var(--radix-select-trigger-height)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  truncate = true,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & { truncate?: boolean }) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'focus:bg-accent text-button-sm relative flex cursor-pointer items-center gap-2 rounded p-2 pr-8 outline-none select-none hover:!bg-neutral-100 hover:!text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:text-indigo-500 data-[state=checked]:hover:!text-indigo-500',
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 grid h-4 w-4 place-items-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText asChild>
        <span className={cn('block max-w-full min-w-0', truncate && 'truncate')}>{children}</span>
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
